// /admin/consultas-comerciales — gestión de leads B2B captados desde landing + tienda.
// Tabla con filtros por estado, click en fila abre detalle + formulario para actualizar estado y notas.

import { useEffect, useState, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

// Mapea tipo_local (text libre de la consulta) → tipo (enum empresa_tipo).
// edificio → administradora porque casi siempre son admins de propiedad horizontal.
const MAP_TIPO = {
  comercio: 'comercio',
  oficina: 'oficina',
  edificio: 'administradora',
  gastronomico: 'comercio',
  industrial: 'otro',
  otro: 'otro',
}

const ESTADOS = [
  { v: 'nuevo',            l: 'Nuevo',            color: '#0EA5E9', bg: '#E0F2FE' },
  { v: 'contactado',       l: 'Contactado',       color: '#7C3AED', bg: '#EDE9FE' },
  { v: 'visita_agendada',  l: 'Visita agendada',  color: '#0891B2', bg: '#CFFAFE' },
  { v: 'cotizado',         l: 'Cotizado',         color: '#CA8A04', bg: '#FEF9C3' },
  { v: 'ganado',           l: 'Ganado',           color: '#16A34A', bg: '#DCFCE7' },
  { v: 'perdido',          l: 'Perdido',          color: '#DC2626', bg: '#FEE2E2' },
  { v: 'descartado',       l: 'Descartado',       color: '#64748B', bg: '#F1F5F9' },
]

const TIPOS_LOCAL_LABEL = {
  oficina: 'Oficina', comercio: 'Comercio', edificio: 'Edificio',
  gastronomico: 'Gastronomía', industrial: 'Industria', otro: 'Otro',
}

const FRECUENCIA_LABEL = {
  semanal: 'Semanal', quincenal: 'Quincenal', mensual: 'Mensual', a_definir: 'A definir',
}

export default function ConsultasComerciales() {
  const [consultas, setConsultas] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [searchParams] = useSearchParams()
  // Deep-link desde dashboard: ?estado=nuevo
  const estadoInicial = (() => {
    const p = searchParams.get('estado')
    return ESTADOS.some(e => e.v === p) ? p : 'todos'
  })()
  const [filtroEstado, setFiltroEstado] = useState(estadoInicial)
  const [busqueda, setBusqueda] = useState('')
  const [detalleId, setDetalleId] = useState(null)

  async function cargar() {
    setCargando(true)
    setError(null)
    const { data, error: e } = await supabase
      .from('consultas_comerciales')
      .select('*')
      .order('created_at', { ascending: false })
    if (e) setError(e.message)
    else setConsultas(data || [])
    setCargando(false)
  }

  useEffect(() => { cargar() }, [])

  const filtradas = useMemo(() => {
    let f = consultas
    if (filtroEstado !== 'todos') f = f.filter(c => c.estado === filtroEstado)
    if (busqueda) {
      const q = busqueda.toLowerCase()
      f = f.filter(c =>
        c.nombre?.toLowerCase().includes(q) ||
        c.empresa?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.telefono?.includes(q) ||
        c.barrio?.toLowerCase().includes(q)
      )
    }
    return f
  }, [consultas, filtroEstado, busqueda])

  const conteoPorEstado = useMemo(() => {
    const c = { todos: consultas.length }
    ESTADOS.forEach(e => { c[e.v] = consultas.filter(x => x.estado === e.v).length })
    return c
  }, [consultas])

  const detalle = detalleId ? consultas.find(c => c.id === detalleId) : null
  const nav = useNavigate()

  return (
    <AdminLayout title="Consultas comerciales">
      <Estilos />
      <div className="cc-page">
        <header className="cc-header">
          <div>
            <h1>Consultas comerciales</h1>
            <p>Leads B2B desde landing y tienda — cotizaciones a medida para comercios, oficinas, edificios.</p>
          </div>
          <button onClick={cargar} className="cc-refresh">↻ Actualizar</button>
        </header>

        <div className="cc-filters">
          <FiltroChip label={`Todos · ${conteoPorEstado.todos}`} active={filtroEstado === 'todos'} onClick={() => setFiltroEstado('todos')} />
          {ESTADOS.map(e => (
            <FiltroChip key={e.v} label={`${e.l} · ${conteoPorEstado[e.v]}`} active={filtroEstado === e.v} onClick={() => setFiltroEstado(e.v)} color={e.color} bg={e.bg} />
          ))}
        </div>

        <input
          type="search"
          placeholder="Buscar por nombre, empresa, email, teléfono, barrio..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          className="cc-search"
        />

        {error && <div className="cc-error">{error}</div>}

        {cargando ? (
          <div className="cc-loading">Cargando consultas...</div>
        ) : filtradas.length === 0 ? (
          <div className="cc-empty">
            <div style={{ fontSize: 48 }}>📭</div>
            <h3>{consultas.length === 0 ? 'Todavía no hay consultas' : 'Sin resultados'}</h3>
            <p>{consultas.length === 0 ? 'Cuando alguien llene el formulario en /cotizar, aparece acá.' : 'Probá con otro filtro o búsqueda.'}</p>
          </div>
        ) : (
          <table className="cc-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Empresa</th>
                <th>Contacto</th>
                <th>Local</th>
                <th>Barrio</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map(c => {
                const estado = ESTADOS.find(e => e.v === c.estado) || ESTADOS[0]
                return (
                  <tr key={c.id} onClick={() => setDetalleId(c.id)} className="cc-row">
                    <td className="cc-fecha">{formatFecha(c.created_at)}</td>
                    <td><strong>{c.empresa}</strong></td>
                    <td>
                      <div>{c.nombre}</div>
                      <div className="cc-contacto">{c.telefono} · {c.email}</div>
                    </td>
                    <td>
                      <div>{TIPOS_LOCAL_LABEL[c.tipo_local] || c.tipo_local}</div>
                      {(c.m2_aprox || c.cantidad_personas) && (
                        <div className="cc-meta">
                          {c.m2_aprox && `${c.m2_aprox}m²`}{c.m2_aprox && c.cantidad_personas && ' · '}{c.cantidad_personas && `${c.cantidad_personas} pers.`}
                        </div>
                      )}
                    </td>
                    <td>{c.barrio || '—'}</td>
                    <td>
                      <span className="cc-badge" style={{ background: estado.bg, color: estado.color }}>{estado.l}</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {detalle && <Detalle consulta={detalle} onClose={() => setDetalleId(null)} onUpdate={cargar} nav={nav} />}
      </div>
    </AdminLayout>
  )
}

function FiltroChip({ label, active, onClick, color = '#1A3461', bg = '#E2E8F0' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cc-chip${active ? ' active' : ''}`}
      style={active ? { background: color, color: '#fff' } : { background: bg, color: '#1A3461' }}
    >
      {label}
    </button>
  )
}

function Detalle({ consulta, onClose, onUpdate, nav }) {
  const [estado, setEstado] = useState(consulta.estado)
  const [notas, setNotas] = useState(consulta.notas_admin || '')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)
  const [empresaLinkeada, setEmpresaLinkeada] = useState(null)
  const [convirtiendo, setConvirtiendo] = useState(false)

  // Buscar si ya hay una empresa creada desde este lead
  useEffect(() => {
    let cancelado = false
    supabase
      .from('empresas')
      .select('id, razon_social, estado_pipeline')
      .eq('consulta_origen_id', consulta.id)
      .maybeSingle()
      .then(({ data }) => { if (!cancelado) setEmpresaLinkeada(data || null) })
    return () => { cancelado = true }
  }, [consulta.id])

  async function convertirEnEmpresa() {
    if (!confirm(`Convertir "${consulta.empresa}" en una empresa B2B? Se creará el registro con los datos de esta consulta precargados.`)) return
    setConvirtiendo(true)
    setError(null)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const tipoEmpresa = MAP_TIPO[consulta.tipo_local] || 'otro'
      // Armar el campo notas combinando el mensaje del lead + frecuencia/m2/personas si los hay
      const partesNotas = []
      if (consulta.mensaje) partesNotas.push(`📥 Mensaje original del lead:\n${consulta.mensaje}`)
      const detalles = []
      if (consulta.m2_aprox) detalles.push(`${consulta.m2_aprox} m²`)
      if (consulta.cantidad_personas) detalles.push(`${consulta.cantidad_personas} personas`)
      if (consulta.frecuencia) detalles.push(`Frecuencia: ${FRECUENCIA_LABEL[consulta.frecuencia] || consulta.frecuencia}`)
      if (consulta.barrio) detalles.push(`Barrio: ${consulta.barrio}`)
      if (detalles.length) partesNotas.push(`📋 Detalles del lead: ${detalles.join(' · ')}`)
      if (consulta.notas_admin) partesNotas.push(`📝 Notas previas:\n${consulta.notas_admin}`)

      const { data: nueva, error: insErr } = await supabase
        .from('empresas')
        .insert({
          razon_social: consulta.empresa,
          tipo: tipoEmpresa,
          telefono: consulta.telefono || null,
          email_facturacion: consulta.email || null,
          contacto_principal_nombre: consulta.nombre || null,
          estado_pipeline: 'cotizando', // ya hubo contacto, no es prospect frío
          owner_id: user?.id || null,
          consulta_origen_id: consulta.id,
          notas: partesNotas.join('\n\n') || null,
          created_by: user?.id || null,
        })
        .select('id')
        .single()
      if (insErr) throw insErr

      // Marcar la consulta como contactada (si todavía estaba en "nuevo")
      const patchConsulta = {}
      if (consulta.estado === 'nuevo') {
        patchConsulta.estado = 'contactado'
        patchConsulta.contactado_at = new Date().toISOString()
      }
      // Sumar nota en la consulta linkeando la empresa
      const notaAuto = `\n\n[${new Date().toLocaleDateString('es-UY')}] Convertido a empresa B2B.`
      patchConsulta.notas_admin = (consulta.notas_admin || '') + notaAuto

      await supabase.from('consultas_comerciales').update(patchConsulta).eq('id', consulta.id)

      // Registrar la conversión como interacción en el log CRM de la empresa
      await supabase.from('empresas_interacciones').insert({
        empresa_id: nueva.id,
        tipo: 'nota',
        autor_id: user?.id || null,
        resumen: 'Lead convertido en empresa',
        notas: `Origen: ${consulta.origen || 'formulario público /cotizar'}.\nRecibida: ${formatFecha(consulta.created_at)}.\nMensaje: ${consulta.mensaje || '(sin mensaje)'}`,
      })

      // Redirigir al detalle de la nueva empresa
      onClose()
      nav(`/admin/empresas/${nueva.id}`)
    } catch (err) {
      setError(err.message || 'No se pudo convertir el lead')
      setConvirtiendo(false)
    }
  }

  async function guardar() {
    setGuardando(true)
    setError(null)
    const patch = { estado, notas_admin: notas }
    const ahora = new Date().toISOString()
    if (estado === 'contactado' && !consulta.contactado_at) patch.contactado_at = ahora
    if (estado === 'cotizado' && !consulta.cotizado_at) patch.cotizado_at = ahora
    if (['ganado', 'perdido', 'descartado'].includes(estado) && !consulta.cerrado_at) patch.cerrado_at = ahora

    const { error: e } = await supabase
      .from('consultas_comerciales')
      .update(patch)
      .eq('id', consulta.id)
    if (e) {
      setError(e.message)
      setGuardando(false)
    } else {
      onUpdate()
      onClose()
    }
  }

  const wa = `https://wa.me/${consulta.telefono.replace(/\D/g, '').replace(/^0/, '598')}?text=${encodeURIComponent(`Hola ${consulta.nombre.split(' ')[0]}, te escribo de Limpack 👋 Te contactamos por la consulta que hiciste para ${consulta.empresa}. ¿Tenés un momento para charlar?`)}`

  // Fix 7-jul-2026 (Nico): botón Email no hacía nada. Ahora arma mailto con
  // subject + body pre-cargados. Si el sistema no tiene handler default,
  // hacemos fallback copiando el email al clipboard con un aviso.
  const primerNombre = (consulta.nombre || '').split(' ')[0]
  const mailSubject = `Limpack — Respuesta a tu consulta de ${consulta.empresa || 'tu empresa'}`
  const mailBody = `Hola ${primerNombre},

Soy Nico de Limpack. Te escribo por la consulta que hiciste desde nuestra web para ${consulta.empresa || 'tu empresa'}.

Nos interesa entender mejor tus necesidades para armarte una propuesta a medida.

¿Podríamos coordinar una llamada rápida esta semana? También podemos seguir por WhatsApp al 098 610 001 si te queda más cómodo.

Saludos,
Nicolás
Limpack — Limpieza, resuelta cada mes.
https://limpack.uy`

  // Fix 7-jul-2026 (Nico): Mac de Nico no tiene Mail.app default → mailto: no
  // abre nada. Cambio a Gmail direct compose (equipo Limpack usa Gmail).
  // Abre nueva pestaña con destinatario + asunto + body pre-cargados en la
  // interfaz web de Gmail. Se puede editar antes de enviar.
  const mailto = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(consulta.email)}&su=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(mailBody)}`

  return (
    <div className="cc-modal-overlay" onClick={onClose}>
      <div className="cc-modal" onClick={e => e.stopPropagation()}>
        <header className="cc-modal-head">
          <div>
            <h2>{consulta.empresa}</h2>
            <div className="cc-modal-fecha">Recibida {formatFecha(consulta.created_at)} · {consulta.origen}</div>
          </div>
          <button onClick={onClose} className="cc-modal-close">✕</button>
        </header>

        <section className="cc-modal-body">
          <div className="cc-modal-grid">
            <Dato label="Contacto" valor={consulta.nombre} />
            <Dato label="Tipo de local" valor={TIPOS_LOCAL_LABEL[consulta.tipo_local] || consulta.tipo_local} />
            <Dato label="Teléfono" valor={<a href={`tel:${consulta.telefono}`}>{consulta.telefono}</a>} />
            <Dato label="Email" valor={<a href={`mailto:${consulta.email}`}>{consulta.email}</a>} />
            <Dato label="M²" valor={consulta.m2_aprox ? `${consulta.m2_aprox} m²` : '—'} />
            <Dato label="Personas" valor={consulta.cantidad_personas || '—'} />
            <Dato label="Frecuencia" valor={FRECUENCIA_LABEL[consulta.frecuencia] || '—'} />
            <Dato label="Barrio" valor={consulta.barrio || '—'} />
          </div>

          {consulta.mensaje && (
            <div className="cc-modal-mensaje">
              <div className="cc-modal-label">Mensaje</div>
              <p>{consulta.mensaje}</p>
            </div>
          )}

          <div className="cc-modal-actions-quick">
            <a href={wa} target="_blank" rel="noopener" className="cc-wa-btn">💬 WhatsApp</a>
            <a href={mailto} target="_blank" rel="noopener" className="cc-mail-btn">✉ Email</a>
          </div>

          {empresaLinkeada ? (
            <div className="cc-empresa-link">
              <div>
                <strong>🏢 Empresa vinculada:</strong> {empresaLinkeada.razon_social}
                <span className="cc-empresa-pipeline">{empresaLinkeada.estado_pipeline}</span>
              </div>
              <button onClick={() => { onClose(); nav(`/admin/empresas/${empresaLinkeada.id}`) }} className="cc-empresa-btn">
                Ver empresa →
              </button>
            </div>
          ) : (
            <div className="cc-convert">
              <div className="cc-convert-info">
                <strong>¿Avanza?</strong> Convertilo en una empresa B2B y queda en el pipeline con todos los datos del lead precargados.
              </div>
              <button onClick={convertirEnEmpresa} disabled={convirtiendo} className="cc-convert-btn">
                {convirtiendo ? 'Convirtiendo…' : '+ Convertir en empresa'}
              </button>
            </div>
          )}

          <hr className="cc-divider" />

          <div className="cc-modal-form">
            <label>
              <span>Estado</span>
              <select value={estado} onChange={e => setEstado(e.target.value)}>
                {ESTADOS.map(e => <option key={e.v} value={e.v}>{e.l}</option>)}
              </select>
            </label>

            <label>
              <span>Notas internas</span>
              <textarea
                value={notas}
                onChange={e => setNotas(e.target.value)}
                placeholder="Ej: hablamos por WA el 23/5, presupuesto enviado el 25/5..."
                rows={4}
              />
            </label>

            {error && <div className="cc-error">{error}</div>}

            <button onClick={guardar} disabled={guardando} className="cc-save">
              {guardando ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}

function Dato({ label, valor }) {
  return (
    <div>
      <div className="cc-modal-label">{label}</div>
      <div className="cc-modal-valor">{valor}</div>
    </div>
  )
}

function formatFecha(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('es-UY', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function Estilos() {
  return (
    <style>{`
      .cc-page { padding: 24px; max-width: 1200px; margin: 0 auto; font-family: Inter, system-ui, sans-serif; color: #1A3461; }
      .cc-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 20px; flex-wrap: wrap; }
      .cc-header h1 { font-size: 24px; font-weight: 600; margin: 0 0 4px; }
      .cc-header p { font-size: 14px; color: #64748B; margin: 0; }
      .cc-refresh { background: #fff; border: 1px solid #E2E8F0; padding: 8px 14px; border-radius: 8px; font-size: 13px; cursor: pointer; color: #1A3461; }
      .cc-refresh:hover { background: #F8FAFC; }

      .cc-filters { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 14px; }
      .cc-chip { border: none; padding: 6px 12px; border-radius: 999px; font-size: 12.5px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
      .cc-chip:hover { opacity: 0.85; }

      .cc-search { width: 100%; padding: 10px 14px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; margin-bottom: 16px; background: #fff; font-family: inherit; }
      .cc-search:focus { outline: none; border-color: #00A8B5; box-shadow: 0 0 0 3px #00A8B533; }

      .cc-error { background: #FEE2E2; color: #991B1B; padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 14px; }
      .cc-loading, .cc-empty { background: #fff; padding: 40px; text-align: center; border-radius: 12px; color: #64748B; }
      .cc-empty h3 { font-size: 16px; color: #1A3461; margin: 12px 0 4px; }
      .cc-empty p { font-size: 13px; margin: 0; }

      .cc-table { width: 100%; background: #fff; border-radius: 12px; border-collapse: separate; border-spacing: 0; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
      .cc-table th { background: #F8FAFC; padding: 10px 14px; text-align: left; font-size: 11px; font-weight: 600; color: #64748B; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid #E2E8F0; }
      .cc-table td { padding: 14px; font-size: 13.5px; border-bottom: 1px solid #F1F5F9; vertical-align: top; }
      .cc-row { cursor: pointer; transition: background 0.1s; }
      .cc-row:hover { background: #F8FAFC; }
      .cc-fecha { font-size: 12px; color: #64748B; white-space: nowrap; }
      .cc-contacto { font-size: 11.5px; color: #64748B; margin-top: 2px; }
      .cc-meta { font-size: 11.5px; color: #64748B; margin-top: 2px; }
      .cc-badge { display: inline-block; padding: 4px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600; }

      .cc-modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
      .cc-modal { background: #fff; border-radius: 16px; max-width: 640px; width: 100%; max-height: 90vh; overflow-y: auto; }
      .cc-modal-head { display: flex; justify-content: space-between; align-items: flex-start; padding: 20px 24px; border-bottom: 1px solid #F1F5F9; position: sticky; top: 0; background: #fff; }
      .cc-modal-head h2 { font-size: 20px; font-weight: 600; margin: 0; color: #1A3461; }
      .cc-modal-fecha { font-size: 12px; color: #64748B; margin-top: 2px; }
      .cc-modal-close { background: none; border: none; font-size: 22px; cursor: pointer; color: #64748B; padding: 4px 10px; }
      .cc-modal-close:hover { color: #1A3461; }

      .cc-modal-body { padding: 20px 24px; }
      .cc-modal-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 16px; }
      .cc-modal-label { font-size: 11px; font-weight: 600; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 3px; }
      .cc-modal-valor { font-size: 14px; color: #1A3461; }
      .cc-modal-valor a { color: #00A8B5; text-decoration: none; }
      .cc-modal-valor a:hover { text-decoration: underline; }

      .cc-modal-mensaje { background: #F8FAFC; padding: 14px; border-radius: 10px; margin: 14px 0; }
      .cc-modal-mensaje p { margin: 0; font-size: 13.5px; color: #475569; line-height: 1.5; white-space: pre-wrap; }

      .cc-modal-actions-quick { display: flex; gap: 10px; margin: 16px 0; }
      .cc-wa-btn, .cc-mail-btn { flex: 1; text-align: center; padding: 10px; border-radius: 8px; font-size: 13.5px; font-weight: 600; text-decoration: none; transition: opacity 0.15s; }
      .cc-wa-btn { background: #25D366; color: #fff; }
      .cc-mail-btn { background: #1A3461; color: #fff; }
      .cc-wa-btn:hover, .cc-mail-btn:hover { opacity: 0.9; }

      .cc-divider { border: none; border-top: 1px solid #F1F5F9; margin: 18px 0; }

      /* Conversión lead → empresa */
      .cc-convert {
        margin: 14px 0 6px; padding: 14px 16px;
        background: linear-gradient(135deg, #F0FDFA, #ECFCCB);
        border-radius: 10px; border: 1px solid #BBF7D0;
        display: flex; gap: 14px; align-items: center; flex-wrap: wrap;
      }
      .cc-convert-info { flex: 1; min-width: 220px; font-size: 13px; color: #134E4A; line-height: 1.45; }
      .cc-convert-info strong { color: #1A3461; }
      .cc-convert-btn {
        background: #16A34A; color: #fff; border: none;
        padding: 10px 16px; border-radius: 8px; font-weight: 700; font-size: 13px;
        font-family: inherit; cursor: pointer; white-space: nowrap;
        transition: background 0.15s;
      }
      .cc-convert-btn:hover { background: #15803D; }
      .cc-convert-btn:disabled { opacity: 0.5; cursor: wait; }

      /* Empresa ya vinculada */
      .cc-empresa-link {
        margin: 14px 0 6px; padding: 12px 16px;
        background: #DBEAFE; border-radius: 10px; border: 1px solid #93C5FD;
        display: flex; gap: 12px; align-items: center; flex-wrap: wrap;
        font-size: 13px; color: #1E3A8A;
      }
      .cc-empresa-link > div { flex: 1; min-width: 200px; }
      .cc-empresa-link strong { color: #1A3461; }
      .cc-empresa-pipeline {
        display: inline-block; margin-left: 8px;
        background: #fff; padding: 2px 8px; border-radius: 999px;
        font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
        color: #1E40AF;
      }
      .cc-empresa-btn {
        background: #1A3461; color: #fff; border: none;
        padding: 8px 14px; border-radius: 8px; font-weight: 700; font-size: 13px;
        font-family: inherit; cursor: pointer; white-space: nowrap;
      }
      .cc-empresa-btn:hover { background: #1A3461; }

      .cc-modal-form label { display: flex; flex-direction: column; margin-bottom: 14px; }
      .cc-modal-form label span { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px; }
      .cc-modal-form select, .cc-modal-form textarea { padding: 10px 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; color: #1A3461; background: #F8FAFC; font-family: inherit; resize: vertical; }
      .cc-modal-form select:focus, .cc-modal-form textarea:focus { outline: none; border-color: #00A8B5; }

      .cc-save { width: 100%; background: #00A8B5; color: #fff; border: none; padding: 12px; border-radius: 10px; font-weight: 600; font-size: 14.5px; cursor: pointer; transition: background 0.15s; }
      .cc-save:hover:not(:disabled) { background: #008a95; }
      .cc-save:disabled { opacity: 0.6; cursor: wait; }

      @media (max-width: 720px) {
        .cc-page { padding: 16px 12px; }
        .cc-table { font-size: 12px; }
        .cc-table th, .cc-table td { padding: 8px; }
        .cc-modal-grid { grid-template-columns: 1fr; }
      }
    `}</style>
  )
}
